import { useMemo } from 'react';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { getAxiosClient } from '../http.client';
export function useFormSubmissionsClient() {
    const { getToken, isSignedIn } = useAuth();
    const { organization } = useOrganization();
    return useMemo(() => {
        return new FormSubmissionsClient(async () => {
            if (!isSignedIn)
                return "";
            // "getToken" returns the Clerk session JWT
            return await getToken();
        }, organization?.id // Pass organization ID as tenantId
        );
    }, [getToken, isSignedIn, organization?.id]);
}
export class FormSubmissionsClient {
    tokenProvider;
    organizationId;
    apiClient;
    constructor(tokenProvider, organizationId) {
        this.tokenProvider = tokenProvider;
        this.organizationId = organizationId;
    }
    async client() {
        if (this.apiClient) {
            return this.apiClient;
        }
        this.apiClient = getAxiosClient("admin/form-submissions");
        return this.apiClient;
    }
    // List all submissions (optionally filtered by formConfigId)
    async listSubmissions(formKey) {
        try {
            const client = await this.client();
            let url = '/';
            const response = await client.get(url, {
                params: {
                    ...(formKey ? { formKey } : {})
                }
            });
            return response.data;
        }
        catch (error) {
            console.error('Error listing form submissions:', error);
            throw error;
        }
    }
    // Get specific submission by submissionId
    async getSubmission(submissionId) {
        try {
            const client = await this.client();
            const response = await client.get(`/${submissionId}`);
            return response.data;
        }
        catch (error) {
            console.error('Error getting form submission:', error);
            throw error;
        }
    }
    // Update submission
    async updateSubmission(submissionId, updates) {
        try {
            const client = await this.client();
            const response = await client.put(`/${submissionId}`, updates);
            return response.data;
        }
        catch (error) {
            console.error('Error updating form submission:', error);
            throw error;
        }
    }
    // Update submission status (lightweight operation)
    async updateSubmissionStatus(submissionId, status) {
        try {
            const client = await this.client();
            const response = await client.patch(`/${submissionId}/status`, { status });
            return response.data;
        }
        catch (error) {
            console.error('Error updating submission status:', error);
            throw error;
        }
    }
    // Clear validation results
    async clearValidationResults(submissionId) {
        try {
            const client = await this.client();
            const response = await client.delete(`/${submissionId}/validations`);
            return response.data;
        }
        catch (error) {
            console.error('Error clearing validation results:', error);
            throw error;
        }
    }
    // Delete submission
    async deleteSubmission(submissionId) {
        try {
            const client = await this.client();
            const response = await client.delete(`/${submissionId}`);
            return response.data;
        }
        catch (error) {
            console.error('Error deleting form submission:', error);
            throw error;
        }
    }
    // Get submissions for specific form
    async getSubmissionsForForm(formKey) {
        try {
            const client = await this.client();
            const response = await client.get(`/form/${formKey}`);
            return response.data;
        }
        catch (error) {
            console.error('Error getting submissions for form:', error);
            throw error;
        }
    }
    // Get submission statistics for a form
    async getSubmissionStats(formConfigId) {
        try {
            const submissions = await this.listSubmissions(formConfigId);
            if (formConfigId) {
                return {
                    total: submissions.length
                };
            }
            // Group by form if no specific form requested
            const byForm = {};
            submissions.forEach(submission => {
                byForm[submission.formKey] = (byForm[submission.formKey] || 0) + 1;
            });
            return {
                total: submissions.length,
                byForm
            };
        }
        catch (error) {
            console.error('Error getting submission statistics:', error);
            throw error;
        }
    }
    // Get all form submission stats (optimized single API call)
    async getAllFormStats() {
        try {
            const client = await this.client();
            const response = await client.get('/stats');
            return response.data;
        }
        catch (error) {
            console.error('Error getting all form stats:', error);
            throw error;
        }
    }
    // Get comprehensive dashboard statistics
    async getDashboardStats(days) {
        try {
            const client = await this.client();
            const params = days ? { days: days.toString() } : {};
            const response = await client.get('/dashboard', { params });
            return response.data;
        }
        catch (error) {
            console.error('Error getting dashboard stats:', error);
            throw error;
        }
    }
    // Get recent submissions
    async getRecentSubmissions(limit = 10, formConfigId) {
        try {
            const submissions = await this.listSubmissions(formConfigId);
            // Sort by sentDate descending and limit
            return submissions
                .sort((a, b) => new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime())
                .slice(0, limit);
        }
        catch (error) {
            console.error('Error getting recent submissions:', error);
            throw error;
        }
    }
    // Get signed URL for attachment
    async getAttachmentSignedUrl(submissionId, attachmentIndex) {
        try {
            const client = await this.client();
            const response = await client.get(`/${submissionId}/attachment/${attachmentIndex}/signed-url`);
            return response.data.signedUrl;
        }
        catch (error) {
            console.error('Error getting attachment signed URL:', error);
            throw error;
        }
    }
}
